import Vue from 'vue'
import Vuex from 'vuex'

import axios from '@/common/axios'
import { setAuthorizationHeader } from '@/common/utilities'

import categoryData from '@/data/categories.json'
import currencyData from '@/data/currency.json'

Vue.use(Vuex)

const ADD_PRODUCTS = 'ADD_PRODUCTS'
const REMOVE_PRODUCTS = 'REMOVE_PRODUCTS'

const SET_PAA_INFO = 'SET_PAA_INFO'
const REMOVE_PAA_INFO = 'REMOVE_PAA_INFO'

const ADD_STARRED_ITEMS = 'ADD_STARRED_ITEMS'
const ADD_STARRED_ITEM = 'ADD_STARRED_ITEM'
const REMOVE_STARRED_ITEM = 'REMOVE_STARRED_ITEM'

const products = {
    namespaced: true,
    state: {
        selection: {
            query: null,
            category: null,
            subcategory: null,
            page: 1
        },
        categories: categoryData,
        products: [],
        paaInfo: {
            totalResults: null,
            totalPages: null,
            moreSearchResultsUrl: null
        },
        starredItems: [],
        currencyArray: currencyData,
        selectedCurrency: {
            "symbol": "$",
            "name": "US Dollar",
            "symbol_native": "$",
            "decimal_digits": 2,
            "rounding": 0,
            "code": "USD",
            "name_plural": "US dollars"
        }
    },
    mutations: {
        UPDATE_SELECTION(state, data) {
            state.selection = Object.assign({}, data)
        },
        ADD_PRODUCTS (state, data) {
            state.products = state.products.concat(data)
        },
        REMOVE_PRODUCTS (state, data) {
            state.products = []
        },
        SET_PAA_INFO (state, data) {
            state.paaInfo.totalResults = data.totalResults
            state.paaInfo.totalPages = data.totalPages
            state.paaInfo.moreSearchResultsUrl = data.moreSearchResultsUrl
        },
        REMOVE_PAA_INFO (state, data) {
            state.paaInfo.totalResults = null
            state.paaInfo.totalPages = null
            state.paaInfo.moreSearchResultsUrl = null
        },
        ADD_STARRED_ITEMS(state, data) {
            state.starredItems = data.starredItems
        },
        ADD_STARRED_ITEM(state, data) {
            state.starredItems.push(data.starredItem)
        },
        REMOVE_STARRED_ITEM(state, data) {
            let newArray = []
            for (let item of state.starredItems) {
                if (item.asin !== data.starredItem.asin) {
                    newArray.push(item)
                }
            }

            state.starredItems = newArray
        },
        UPDATE_SELECTED_CURRENCY(state, data) {
            state.selectedCurrency = Object.assign({}, data)
        },
    },
    getters: {
        selection (state) {
            return state.selection
        },
        nestedAllCategories (state) {
            const createDataTree = dataset => {
                let hashTable = Object.create(null)
                dataset.forEach( aData => hashTable[aData.browseNodeId] = { ...aData, children : [] } )
                let dataTree = []

                dataset.forEach( aData => {
                    if ( aData.parent ) {
                        hashTable[aData.parent].children.push(hashTable[aData.browseNodeId])
                    } else {
                        dataTree.push(hashTable[aData.browseNodeId])
                    }
                })
                return dataTree
            }

            return createDataTree(state.categories)
        },
        rootCategories (state) {
            let rootCategories = []
            for (let cat of state.categories) {
                if (cat.isRoot) {
                    rootCategories.push(cat)
                }
            }

            return rootCategories
        },
        products (state, getters) {
            let productsWithStars = state.products
            for (let product of productsWithStars) {
                if (getters.starredItemIds.includes(product.ASIN)) {
                    product.isStarred = true
                } else {
                    product.isStarred = false
                }
            }

            return productsWithStars
        },
        paaInfo (state) {
            return state.paaInfo
        },
        starredItems (state) {
            // Remove duplcates
            const uniqueArray = (arr, objKey) => arr.filter((obj, key, array) => array.map((obj2) => obj[`${objKey}`] !== obj2[`${objKey}`] ))

            let tempArray = uniqueArray(state.starredItems)
            let disassociatedTempArray = JSON.parse(JSON.stringify(tempArray))

            let starredItemsWithStarsArray = []
            for (let item of disassociatedTempArray) {
                let tempItemData = JSON.parse(item.data)
                tempItemData.isStarred = true
                tempItemData = JSON.stringify(tempItemData)
                item.data = tempItemData
                starredItemsWithStarsArray.push(item)
            }

            return starredItemsWithStarsArray
        },
        starredItemIds (state, getters) {
            let starredItemIds = []
            for (let item of getters.starredItems) {
                starredItemIds.push(item.asin)
            }
            return starredItemIds
        },
        currencyArray (state) {
            return state.currencyArray
        },
        selectedCurrency (state) {
            return state.selectedCurrency
        }
    },
    actions: {
        // API
        async getItems ({ dispatch, commit, getters, rootGetters }, data) {
            try {
                const response = await axios.post('/paa/products/item/top', {
                    query: data.selection.query,
                    category: data.selection.category,
                    subcategory: data.selection.subcategory,
                    page: data.selection.page
                })

                commit(SET_PAA_INFO, {
                    totalResults: response.data.totalResults,
                    totalPages: response.data.totalPages,
                    moreSearchResultsUrl: response.data.moreSearchResultsUrl
                })
                commit(ADD_PRODUCTS, JSON.parse(response.data.items))

                return JSON.parse(response.data.items)
            } catch (error) {
                if (error && error.response && error.response.data && error.response.data.message) {
                    throw new Error(error.response.data.message)
                } else {
                    throw new Error('ERROR')
                }
            }
        },
        async starItem ({ dispatch, commit, getters, rootGetters }, data) {
            try {
                setAuthorizationHeader(rootGetters['user/accessToken'])
                await axios.post('/paa/products/item/star', {
                    asin: data.asin,
                    data: data.data
                })
                commit(ADD_STARRED_ITEM, {starredItem: {asin: data.asin, data: data.data}})
            } catch (error) {
                if (error && error.response && error.response.data && error.response.data.message) {
                    throw new Error(error.response.data.message)
                } else {
                    throw new Error('ERROR')
                }
            }
        },
        async unstarItem ({ dispatch, commit, getters, rootGetters }, data) {
            try {
                setAuthorizationHeader(rootGetters['user/accessToken'])
                await axios.delete('/paa/products/item/star', {
                    asin: data.asin,
                    data: data.data
                })
                commit(REMOVE_STARRED_ITEM, {starredItem: {asin: data.asin, data: data.data}})
            } catch (error) {
                if (error && error.response && error.response.data && error.response.data.message) {
                    throw new Error(error.response.data.message)
                } else {
                    throw new Error('ERROR')
                }
            }
        },
        async getStarredItems ({ dispatch, commit, getters, rootGetters }, data) {
            try {
                setAuthorizationHeader(rootGetters['user/accessToken'])
                const result = await axios.get('/paa/products/item/star')
                commit(ADD_STARRED_ITEMS, {starredItems: result.data.starredItems})
            } catch (error) {
                if (error && error.response && error.response.data && error.response.data.message) {
                    throw new Error(error.response.data.message)
                } else {
                    throw new Error('ERROR')
                }
            }
        },

        // Just Mutations
        async removeItems ({ dispatch, commit, getters, rootGetters }, data) {
            return new Promise((resolve, reject) => {
                commit(REMOVE_PRODUCTS)
                return resolve()
            })
        },
        async removePaaInfo ({ dispatch, commit, getters, rootGetters }, data) {
            return new Promise((resolve, reject) => {
                commit(REMOVE_PAA_INFO)
                return resolve()
            })
        }
    }
}

export default products
